import { Link, useNavigate } from 'react-router';
import {
  useAgentChatSession,
  useAgents,
  useModelChoices,
  useSetAgentModel,
  type AgentSummary,
} from '../hooks/useApi';

const shortModel = (id: string) => id.replace('claude-', '').replace('-20251001', '');

/**
 * Página `/agents` — catálogo visual de los agentes disponibles en xVision.
 *
 * Agrupa en 2 secciones:
 *  - 🎯 xVision (producto): los 8 agentes especializados por tipo de asset (Capa 3)
 *  - 🔬 fal.ai general (research / consultoría): los 12 agentes transversales (Capa 2)
 *
 * Cada card muestra slug, nombre amigable, descripción del frontmatter, y un botón
 * "Abrir chat" que llama POST /agents/:slug/chat-session — el backend devuelve
 * (idempotente) el chatId de una shell-instance sandbox por (user, agente) en el
 * proyecto interno `__sandbox-chats`. Los assets generados ahí quedan asociados
 * al user y aparecen en sus galerías globales (/backgrounds, /character-creator, etc.).
 */

const AGENT_LABEL_MAP: Record<string, string> = {
  // xVision (producto)
  'xvision-360': '🌐 Imagen 360',
  'xvision-personaje': '👤 Personajes',
  'xvision-environment': '🏞️ Escenarios',
  'xvision-utilidades': '🛠️ Utilidades',
  'xvision-armonizador': '✨ Armonizador',
  'xvision-voz': '🎤 Voces',
  'xvision-video': '🎬 Videos',
  'xvision-lipsync': '🗣️ Lipsync',
  // fal.ai general
  'fal-360-expert': 'Experto 360 (research)',
  'fal-image-expert': 'Experto imagen (research)',
  'fal-video-expert': 'Experto video (research)',
  'fal-audio-expert': 'Experto audio (research)',
  'fal-avatar-expert': 'Experto avatar (research)',
  'fal-3d-expert': 'Experto 3D (research)',
  'fal-pipeline-orchestrator': 'Pipeline multi-paso',
  'fal-prompt-engineer': 'Prompt engineer',
  'fal-cost-auditor': 'Auditoría de costos',
  'fal-model-selector': 'Recomendador de modelos',
  'fal-docs-watcher': 'Watcher de docs fal.ai',
  'fal-studio-architect': 'Arquitecto Studio',
};

function labelFor(slug: string): string {
  return AGENT_LABEL_MAP[slug] ?? slug;
}

function AgentCard({ agent }: { agent: AgentSummary }) {
  const navigate = useNavigate();
  const openChat = useAgentChatSession();
  const { data: choices } = useModelChoices();
  const setModel = useSetAgentModel();

  const handleOpen = async () => {
    try {
      const { chatId } = await openChat.mutateAsync(agent.slug);
      navigate(`/chats/${chatId}`);
    } catch (err) {
      alert(`No se pudo abrir el chat con ${agent.slug}: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <div className="border border-slate-700 rounded-lg p-4 bg-slate-900 hover:border-vision transition-colors flex flex-col gap-2">
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-base font-semibold text-slate-100">{labelFor(agent.slug)}</h3>
        <select
          value={agent.overridden ? agent.model : ''}
          onChange={(e) => void setModel.mutateAsync({ slug: agent.slug, model: e.target.value || null })}
          disabled={setModel.isPending}
          title={`Modelo global del agente (Default = el del .md: ${shortModel(agent.defaultModel)})`}
          className="text-[10px] font-mono px-1 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 focus:outline-none focus:border-vision max-w-[130px] disabled:opacity-50"
        >
          <option value="">Default ({shortModel(agent.defaultModel)})</option>
          {choices?.map((m) => (
            <option key={m.id} value={m.id}>
              {shortModel(m.id)}
            </option>
          ))}
        </select>
      </div>
      {agent.overridden && (
        <div className="text-[10px] text-amber-400">
          ⚡ override global activo · default {shortModel(agent.defaultModel)}
        </div>
      )}
      <p className="text-xs text-slate-400 line-clamp-4">{agent.description}</p>
      <div className="flex items-center justify-between mt-auto pt-2">
        <code className="text-[10px] text-slate-500 font-mono">{agent.slug}</code>
        <button
          disabled={openChat.isPending}
          className="px-3 py-1 rounded bg-vision/20 hover:bg-vision/40 text-vision text-xs font-medium border border-vision/40 disabled:opacity-50"
          onClick={handleOpen}
        >
          {openChat.isPending ? 'Abriendo…' : 'Abrir chat →'}
        </button>
      </div>
    </div>
  );
}

export function AgentsPage() {
  const { data: agents, isLoading, error } = useAgents();

  const xvisionAgents = (agents ?? []).filter((a) => a.category === 'xvision');
  const falAgents = (agents ?? []).filter((a) => a.category === 'fal');
  const otherAgents = (agents ?? []).filter((a) => a.category === 'other');

  return (
    <div className="p-3 sm:p-6 max-w-6xl mx-auto">
      <header className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl font-bold text-slate-100">Agentes</h1>
          <Link to="/projects" className="text-xs text-slate-400 hover:text-vision underline">
            ← volver a proyectos
          </Link>
        </div>
        <p className="text-sm text-slate-400">
          Cada agente tiene un rol estricto y conoce sus capacidades. Cuando arrancás un chat con
          uno, se presenta y te guía con preguntas si faltan parámetros.
        </p>
      </header>

      {isLoading && <div className="text-slate-400 text-sm">Cargando agentes…</div>}
      {error && (
        <div className="text-red-400 text-sm">
          Error al cargar agentes: {error instanceof Error ? error.message : String(error)}
        </div>
      )}

      {agents && (
        <>
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-slate-200 mb-3">
              🎯 xVision <span className="text-xs text-slate-500 font-normal">— producción, uno por tipo de asset</span>
            </h2>
            {xvisionAgents.length === 0 ? (
              <div className="text-slate-500 text-sm italic">
                No hay agentes xvision-* cargados. Verificá que se ejecutó{' '}
                <code className="text-vision">node apps/api/scripts/sync-prompts.js</code>.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {xvisionAgents.map((a) => (
                  <AgentCard key={a.slug} agent={a} />
                ))}
              </div>
            )}
          </section>

          <section className="mb-8">
            <h2 className="text-lg font-semibold text-slate-200 mb-3">
              🔬 fal.ai general <span className="text-xs text-slate-500 font-normal">— research / consultoría / utilidades transversales</span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {falAgents.map((a) => (
                <AgentCard key={a.slug} agent={a} />
              ))}
            </div>
          </section>

          {otherAgents.length > 0 && (
            <section>
              <h2 className="text-lg font-semibold text-slate-200 mb-3">Otros</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {otherAgents.map((a) => (
                  <AgentCard key={a.slug} agent={a} />
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
